/**
 * Accordion Component
 *
 * Light DOM Web Component for expand/collapse accordion behavior.
 * Uses global CSS utilities for styling flexibility.
 *
 * Features:
 * - Toggle aria-expanded on trigger click
 * - Keyboard accessible (Enter/Space to toggle)
 * - Icon rotation on expand via CSS class
 * - Smooth max-height transition for content
 * - Optional `open` attribute to start expanded
 *
 * Usage:
 *   <felix-accordion>
 *     <button class="accordion__trigger">Title</button>
 *     <div class="accordion__content">Content here...</div>
 *   </felix-accordion>
 *
 * Attributes:
 *   open - If present, accordion starts expanded (default: collapsed)
 *
 * Example (starts closed):
 *   <felix-accordion>...</felix-accordion>
 *
 * Example (starts open):
 *   <felix-accordion open>...</felix-accordion>
 *
 * CSS Classes Applied:
 *   .accordion--expanded - Added when accordion is open
 *
 * Events Emitted:
 *   accordion-toggle - Fires on toggle with { detail: { expanded: boolean } }
 *
 * @package Felix
 * @since 1.0.0
 */

class FelixAccordion extends HTMLElement {
	connectedCallback() {
		this.trigger = this.querySelector('.accordion__trigger')
		this.content = this.querySelector('.accordion__content')

		if (!this.trigger || !this.content) {
			return
		}

		// Check for `open` attribute to determine initial state
		const startsOpen = this.hasAttribute('open')
		this.isExpanded = startsOpen

		// Set initial visual state
		if (startsOpen) {
			this.classList.add('accordion--expanded')
			this.trigger.setAttribute('aria-expanded', 'true')
		} else {
			this.trigger.setAttribute('aria-expanded', 'false')
		}

		// Bind event handlers
		this.handleClick = this.handleClick.bind(this)
		this.handleKeydown = this.handleKeydown.bind(this)

		// Attach events
		this.trigger.addEventListener('click', this.handleClick)
		this.trigger.addEventListener('keydown', this.handleKeydown)

		// Generate unique ID for content if needed
		if (!this.content.id) {
			this.content.id = `accordion-content-${Math.random().toString(36).slice(2, 9)}`
		}
		this.trigger.setAttribute('aria-controls', this.content.id)
	}

	disconnectedCallback() {
		if (this.trigger) {
			this.trigger.removeEventListener('click', this.handleClick)
			this.trigger.removeEventListener('keydown', this.handleKeydown)
		}
	}

	handleClick(e) {
		e.preventDefault()
		this.toggle()
	}

	handleKeydown(e) {
		// Toggle on Enter or Space
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault()
			this.toggle()
		}
	}

	toggle() {
		this.isExpanded = !this.isExpanded
		this.trigger.setAttribute('aria-expanded', this.isExpanded.toString())
		this.classList.toggle('accordion--expanded', this.isExpanded)

		// Update the `open` attribute to reflect current state
		if (this.isExpanded) {
			this.setAttribute('open', '')
		} else {
			this.removeAttribute('open')
		}

		// Dispatch custom event for external listeners
		this.dispatchEvent(
			new CustomEvent('accordion-toggle', {
				detail: { expanded: this.isExpanded },
				bubbles: true,
			})
		)
	}

	// Public methods for programmatic control
	expand() {
		if (!this.isExpanded) {
			this.toggle()
		}
	}

	collapse() {
		if (this.isExpanded) {
			this.toggle()
		}
	}
}

customElements.define('felix-accordion', FelixAccordion)
